import { Injectable } from '@angular/core';
import { DescribeThingCommandOutput } from '@aws-sdk/client-iot';
import * as moment from 'moment';
import { lastValueFrom, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { AnalyticsManagerService } from '../lib/analytics-manager.service';
import { AwsService } from '../lib/aws.service';
import { ThingGroupStatistics } from '../models/backend/thing/thing-group-statistics';
import { CriteriaKey, ParsedCriteriaKey } from '../models/firmware';
import { MacAddress, ThingData, ThingGroup, ThingType } from '../models/thingtype';
import { UuidUtils } from '../shared/utils/uuid.utils';
import { ThingsService } from './backend/services/things/things.service';
import { DatabaseService } from './database.service';
import { ThingTypesService } from './thing-types.service';

export interface StatisticsLine {
  label: string;
  count: number;
}

export interface DeployCount {
  totalCount: number;
  details: DeployCountDetail[];
}

export interface DeployCountDetail {
  count: number;
  label: string;
  criteriaEntries: [key: string, value: string][];
}

@Injectable({
  providedIn: 'root',
})
export class RegistryService {
  constructor(
    private aws: AwsService,
    private thingTypesService: ThingTypesService,
    private dbService: DatabaseService,
    private analyticsManagerService: AnalyticsManagerService,
    private thingsService: ThingsService,
  ) {}

  async getThing(thingName: string): Promise<DescribeThingCommandOutput> {
    return await (await this.aws.iot()).describeThing({ thingName });
  }

  countDeployGroups(thingGroups: ThingGroup[], metaversionId: string): Observable<DeployCountDetail[]> {
    const thingGroupNames = thingGroups.map((_thingGroup) => _thingGroup.thingGroupName);
    return this.thingsService.getThingGroupStatistics(thingGroupNames).pipe(
      map((counts: ThingGroupStatistics): DeployCountDetail[] =>
        counts.map((statistics) => {
          const criteriaKey = statistics.thingGroupName;
          const count = statistics.statistics?.count ?? 0;
          const parsedKey = ParsedCriteriaKey.fromCriteriaKey(criteriaKey as CriteriaKey);
          if (parsedKey.valuesAreOnlyNA()) {
            return {
              count,
              label: `devices with version ${metaversionId}`,
            } as DeployCountDetail;
          }

          const key = ParsedCriteriaKey.fromCriteriaKey(criteriaKey as CriteriaKey);
          const criterias = Object.entries(key).filter(([, value]) => value !== 'NA');

          return {
            count,
            label: 'devices for criteria',
            criteriaEntries: criterias,
          } as DeployCountDetail;
        }),
      ),
    );
  }

  async getDailyNewDevicesStats(thingType: ThingType, numberOfDays: number = 7): Promise<StatisticsLine[]> {
    let allThingTypes: ThingType[] = [];
    if (thingType === '') {
      allThingTypes = (await lastValueFrom(this.thingTypesService.getThingTypes())) as ThingType[];
    }

    const days: string[] = [];
    for (let i = numberOfDays - 1; i >= 0; i--) {
      days.push(moment.utc().subtract(i, 'day').format('YYYY-MM-DD'));
    }

    const items = await this.analyticsManagerService.fetchStatsDays(days);

    return days.map((day) => {
      let count = 0;
      const possiblyUndefined = items.find((_) => _ !== undefined && _.day === day);
      if (possiblyUndefined !== undefined) {
        if (thingType === '') {
          for (const existingThingType of allThingTypes) {
            count += parseFloat(possiblyUndefined[`${existingThingType}_count`] || '0');
          }
        } else {
          count = parseFloat(possiblyUndefined[`${thingType}_count`] || '0');
        }
      }

      return { label: day, count };
    });
  }

  /**
   * Returns the ThingData from its identifier
   *
   * @param searchedThingType the ThingType of reference
   * @param identifier either a macAddress, a full thingName (type + serial), or a SerialNumber
   * @throws an Error if the identifier is wrong, doesn't exist, or its ThingType doesn't match the reference searchedThingType
   */
  public async searchThing(searchedThingType: string, identifier: string): Promise<ThingData | undefined> {
    let foundSerialNumber: string;
    let foundThingType: string;

    if (identifier.includes(':')) {
      // mac address
      const _thing = await this.dbService.search(identifier as MacAddress);
      foundSerialNumber = _thing.serialnumber;
      foundThingType = _thing.thingType;
    } else if (UuidUtils.isValidUuid(identifier)) {
      // plain serial number
      foundSerialNumber = identifier;
      foundThingType = searchedThingType;
    } else {
      // thing name (thingtype + serial)
      const [tmpFoundThingType, ...restFoundSerialNumber] = identifier.split('-');
      foundThingType = tmpFoundThingType;
      foundSerialNumber = restFoundSerialNumber.join('-');
    }

    if (foundThingType !== searchedThingType) {
      throw new Error(
        `Incompatible Thing Type ("${foundThingType}") with the target Thing Type of Group ("${searchedThingType}")`,
      );
    }
    if (!UuidUtils.isValidUuid(foundSerialNumber)) {
      throw new Error(`Given SerialNumber "${foundSerialNumber}" is invalid`);
    }

    const describedThing = await this.getThing(`${searchedThingType}-${foundSerialNumber}`);
    if (!describedThing?.thingArn) {
      throw new Error("Thing doesn't exist");
    }

    return Promise.resolve(ThingData.fromDescribeResponse(describedThing));
  }
}
